import WorkStart from "../../components/WorkStart";
import { useWorkNavigation } from "../../context/WorkNavigationContext";
import { adminWorkActions } from "../../lib/simpleNavigation";
import { useAuth } from "@/context/AuthContext";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, RefreshCw, ShieldAlert, ShieldCheck, Globe, DatabaseBackup, ReceiptText, Inbox,
  MessageSquare, Clock, ArrowRight, ChevronRight, CheckCircle2, AlertTriangle, BarChart3,
  Phone, Mail, Server, Activity, TrendingUp, Wallet, Banknote, CalendarCheck, FileText,
  Users, KanbanSquare, Boxes, Sparkles, Crown, ExternalLink, Zap, ClipboardList, History,
  WifiOff, FileSearch, Loader2, PiggyBank, BadgeCheck, Filter, ArrowDownUp, TrendingDown, Minus, Smartphone, Lightbulb, MonitorX,
} from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, chf } from "@/components/common";
import AnalyticsBoard from "./AnalyticsBoard";
import { useI18n } from "@/context/I18nContext";

// ---------- color system (5-Farben-Regel) ----------
const DOT = { green: "bg-[#A3E635]", amber: "bg-amber-400", red: "bg-red-500", gray: "bg-gray-600" };
const TXT = { green: "text-[#A3E635]", amber: "text-amber-400", red: "text-red-400", gray: "text-gray-500" };
const BORD = {
  green: "border-[#A3E635]/30", amber: "border-amber-500/40", red: "border-red-500/45", gray: "border-white/10",
};
const TINT = {
  green: "bg-[#A3E635]/[0.06]", amber: "bg-amber-500/[0.06]", red: "bg-red-500/[0.07]", gray: "bg-[#121217]",
};
const PRIO = {
  hoch: { label: "Hoch", cls: "text-red-400" }, kritisch: { label: "Kritisch", cls: "text-red-400" },
  mittel: { label: "Mittel", cls: "text-amber-400" }, niedrig: { label: "Niedrig", cls: "text-gray-400" },
};

const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (iso) => (iso ? `${pad(new Date(iso).getHours())}:${pad(new Date(iso).getMinutes())}` : "—");
function smartDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso), now = new Date();
  const same = (a, b) => a.toDateString() === b.toDateString();
  const t = `${pad(d.getHours())}:${pad(d.getMinutes())} Uhr`;
  if (same(d, now)) return `Heute, ${t}`;
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const m = new Date(now); m.setDate(now.getDate() + 1);
  if (same(d, y)) return `Gestern, ${t}`;
  if (same(d, m)) return `Morgen, ${t}`;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${t}`;
}
const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

// ---------- KEWAGPage Control OS — kleine globale Plattform-Statuskarte ----------
const isPreviewEnv = () => {
  const h = typeof window !== "undefined" ? window.location.hostname : "";
  return h.includes("preview.emergentagent.com") || h.includes("localhost") || h.includes("127.0.0.1");
};
const CO_ICON = {
  "Login-Sicherheit": ShieldCheck, "Datenbank": DatabaseBackup, "E-Mail-Versand": Mail,
  "bexio / Rechnungen": Banknote, "System-Backup": Server, "Deployment": Zap,
};

function ControlOsCard({ co, tr }) {
  const items = co?.items || [];
  const preview = isPreviewEnv();
  const meta = [
    { label: tr("Version"), value: co?.version || "—" },
    { label: tr("Environment"), value: co?.environment === "production" && !preview ? "Production" : co?.environment === "preview" && preview ? "Preview" : tr("Nicht verifiziert"), cls: "text-amber-300" },
    { label: tr("Datenmodus"), value: tr("Nicht verifiziert"), cls: "text-gray-400" },
  ];
  return (
    <Card className="p-5" data-testid="control-os-card">
      <div className="flex items-center gap-2 mb-4">
        <Crown className="w-4 h-4 text-[#A3E635]" />
        <span className="text-sm font-bold text-white">{tr("KEWAGPage Control OS")}</span>
        <span className="text-[11px] text-gray-500">· {tr("Globaler Plattform-Status")}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="control-os-items">
        {items.map((it, i) => {
          const c = it.color || "gray";
          const Ic = CO_ICON[it.name] || Server;
          return (
            <div key={i} data-testid={`co-item-${i}`}
              className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 ${BORD[c]} ${TINT[c]}`}>
              <Ic className={`w-4 h-4 mt-0.5 shrink-0 ${TXT[c]}`} />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${DOT[c]}`} />
                  <span className="text-xs font-semibold text-gray-200 truncate">{tr(it.name)}</span>
                </div>
                <div className={`text-[11px] mt-0.5 ${TXT[c]}`}>{tr(it.label)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-white/8 flex items-center gap-5 flex-wrap" data-testid="control-os-meta">
        {meta.map((m, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[11px]" data-testid={`co-meta-${i}`}>
            <span className="text-gray-500">{m.label}:</span>
            <span className={`font-semibold ${m.cls || "text-gray-200"}`}>{m.value}</span>
          </div>
        ))}
        <span className="ml-auto text-[10px] text-gray-600 flex items-center gap-1">
          <Server className="w-3 h-3" /> {tr("Globale Systeme · nicht pro Kunde")}
        </span>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user: authUser } = useAuth();
  const workNavigation = useWorkNavigation();
  const isSuperAdmin = authUser?.role === "super_admin";
  const writableTeamRole = authUser?.role === "team_member"
    && ["admin_ops", "projektleitung", "entwickler", "support", "buchhaltung", "content_seo"].includes(authUser?.team_role);
  const canWriteClients = isSuperAdmin || (writableTeamRole && (authUser?.client_scope === "alle"
    || (authUser?.client_scope === "bestimmte" && (authUser?.scoped_client_ids || []).length > 0)));
  const canCreateClient = isSuperAdmin || (canWriteClients && authUser?.client_scope === "alle");
  const { t: tr } = useI18n();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [cFilter, setCFilter] = useState(() => localStorage.getItem("kewag_dash_filter") || "all");
  const [cSort, setCSort] = useState(() => localStorage.getItem("kewag_dash_sort") || "priority");
  const [pwa, setPwa] = useState(null);
  const [wishStats, setWishStats] = useState(null);
  const [feErr, setFeErr] = useState(null);

  const loadDash = () => api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { loadDash(); }, []);
  useEffect(() => { api.get("/admin/pwa-stats").then((r) => setPwa(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/ai-requests/stats").then((r) => setWishStats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/admin/client-events?type=client_error&limit=50").then((r) => {
      const rows = r.data || [];
      const wk = Date.now() - 7 * 24 * 3600 * 1000;
      const recent = rows.filter((e) => new Date(e.created_at).getTime() >= wk).length;
      const latest = rows[0] ? (rows[0].message || rows[0].path || "").slice(0, 55) : "";
      setFeErr({ recent, latest, total: rows.length });
    }).catch(() => setFeErr(null));
  }, []);
  useEffect(() => { localStorage.setItem("kewag_dash_filter", cFilter); }, [cFilter]);
  useEffect(() => { localStorage.setItem("kewag_dash_sort", cSort); }, [cSort]);

  const checkAll = async () => {
    if (!isSuperAdmin) return;
    setCheckingAll(true);
    toast.loading(tr("Alle Kunden werden live geprüft …"), { id: "ca" });
    try {
      const { data: r } = await api.post("/admin/check-all-connectors");
      await loadDash();
      toast.success(tr("Live-Prüfung fertig · {live} live · {errors} Fehler · {checked} geprüft").replace("{live}", r.live).replace("{errors}", r.errors).replace("{checked}", r.checked), { id: "ca" });
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Live-Prüfung fehlgeschlagen"), { id: "ca" });
    } finally { setCheckingAll(false); }
  };

  const checkOne = async (cid) => {
    if (!canWriteClients) return;
    setCheckingId(cid);
    toast.loading(tr("Kunde wird live geprüft …"), { id: "c1" });
    try {
      await api.post(`/websites/${cid}/check`);
      await loadDash();
      toast.success(tr("Live-Prüfung fertig"), { id: "c1" });
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Live-Prüfung fehlgeschlagen"), { id: "c1" });
    } finally { setCheckingId(null); }
  };

  if (d === null) return <div className="flex items-center justify-center py-32"><Loader2 className="w-7 h-7 animate-spin text-[#A3E635]" /></div>;
  if (d === false) return <div className="text-center py-20 text-gray-500">{tr("Dashboard konnte nicht geladen werden.")}</div>;

  const cr = d.critical || {};
  const cards = d.cards || {};
  const money = d.money || {};
  const wk = d.week || {};
  const au = d.arbeit_umsatz || {};
  const tm = d.team || {};
  const an = d.analytics || {};
  const ft = d.forms_tracking || {};
  const bk = cards.backup || {};

  // --- Kunden im Blick: filtern + sortieren ---
  const ATT_RANK = { rot: 0, gelb: 1, gruen: 2 };
  const allClients = d.attention_clients || [];
  const CMP = {
    priority: (a, b) => (ATT_RANK[a.attention] - ATT_RANK[b.attention]) || ((b.open_invoice_amount || 0) - (a.open_invoice_amount || 0)),
    invoice: (a, b) => (b.open_invoice_amount || 0) - (a.open_invoice_amount || 0),
    leads: (a, b) => (b.open_leads || 0) - (a.open_leads || 0),
    tasks: (a, b) => (b.open_tasks || 0) - (a.open_tasks || 0),
    name: (a, b) => (a.company_name || "").localeCompare(b.company_name || ""),
  };
  const clientRows = allClients
    .filter((cl) => {
      if (cFilter === "problems") return cl.attention !== "gruen";
      if (cFilter === "critical") return cl.attention === "rot";
      if (["aktiv", "in_vorbereitung", "pausiert", "gekuendigt"].includes(cFilter)) return cl.status === cFilter;
      return true;
    })
    .slice().sort(CMP[cSort] || CMP.priority);
  const problemCount = allClients.filter((c) => c.attention !== "gruen").length;
  const FILTERS = [["all", `${tr("Alle")} (${allClients.length})`], ["problems", tr("Mit Problemen ({n})").replace("{n}", problemCount)], ["critical", tr("Kritisch")], ["aktiv", tr("Aktiv")], ["in_vorbereitung", tr("In Vorbereitung")]];
  const SORTS = [["priority", tr("Priorität (kritisch zuerst)")], ["name", tr("Name A–Z")]];

  return (
    <div className="fade-in w-full min-w-0 max-w-full space-y-8" data-testid="admin-dashboard">
      {/* ===== 1. HEADER ===== */}
      <div>
        <PageHeader title={authUser?.role === "super_admin" ? tr("Mein Arbeitsplatz") : tr("Team-Arbeitsplatz")} subtitle={tr("Kunden, Aufgaben und Systemstatus im Überblick.")}>
          {isSuperAdmin && <button onClick={checkAll} disabled={checkingAll} data-testid="dashboard-check-all"
            className="bg-white/5 hover:bg-white/10 border border-white/15 text-white font-bold py-2.5 px-5 rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${checkingAll ? "animate-spin" : ""}`} /> {checkingAll ? tr("Prüfe alle Kunden …") : tr("Alle Kunden live prüfen")}
          </button>}
          {canCreateClient && <Link to="/admin/clients/new" data-testid="dashboard-new-client"
            className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg transition-colors inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> {tr("Kunde anlegen")}
          </Link>}
        </PageHeader>
        <div className="mb-5"><WorkStart actions={adminWorkActions(workNavigation)} readOnly={!canWriteClients} /></div>

        {/* Zusammenfassungsleiste */}
        <div data-testid="dashboard-summary-bar"
          className={`flex items-center gap-3 flex-wrap px-5 py-3 rounded-xl border ${cr.security?.color === "red" ? "border-red-500/40 bg-red-500/[0.06]" : "border-white/10 bg-[#121217]"}`}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-white/5">
            {cr.security?.color === "green" ? <ShieldCheck className="w-4 h-4 text-[#A3E635]" /> : <ShieldAlert className={`w-4 h-4 ${TXT[cr.security?.color] || TXT.gray}`} />}
          </div>
          <span className="text-sm font-semibold text-gray-200">{tr("Heute im Überblick:")}</span>
          <span className="text-sm text-gray-400">{(d.summary_chips || []).join("  ·  ")}</span>
          <Link to="/admin/qa" data-testid="dashboard-security-source" className="text-xs text-gray-400 underline hover:text-white">{tr("Sicherheit & QA")}{d.security?.last_check ? ` · ${smartDate(d.security.last_check)}` : ""}</Link>
          <span className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            {tr("Stand:")} {smartDate(d.stand)}
            <button onClick={loadDash} title={tr("Aktualisieren")} aria-label={tr("Aktualisieren")} data-testid="dashboard-refresh"
              className="p-1 rounded-md hover:bg-white/10"><RefreshCw className={`w-3.5 h-3.5 ${checkingAll ? "animate-spin" : ""}`} /></button>
          </span>
        </div>
      </div>

      <details open data-testid="dashboard-details" className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
        <summary className="cursor-pointer min-h-[44px] text-sm font-bold text-gray-200 focus-visible:outline focus-visible:outline-2">{tr("Team, Kennzahlen und weitere Übersichten")}</summary>
        <div className="space-y-8 pt-5">
      {/* ===== TREND / VERLAUF (KW vs. Vorwoche) ===== */}
      {d.week_trend && <TrendStrip wt={d.week_trend} />}

      {/* ===== 2. HEUTE KRITISCH ===== */}
      <section data-testid="section-critical">
        <SectionTitle>{tr("Heute kritisch")}</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <CritCard testid="crit-security" icon={ShieldAlert} title={tr("Sicherheitswarnungen")} c={cr.security} nav={nav} />
          <CritCard testid="crit-offline" icon={Globe} title={tr("Websites offline")} c={cr.offline} nav={nav} />
          <CritCard testid="crit-backup" icon={DatabaseBackup} title={tr("Backup-Probleme")} c={cr.backup} nav={nav} />
          <CritCard testid="crit-invoices" icon={ReceiptText} title={tr("Offene Rechnungen")} c={cr.invoices} nav={nav} />
          <CritCard testid="crit-leads" icon={Mail} title={tr("Ungelesene Anfragen")} c={cr.leads} nav={nav} />
          <CritCard testid="crit-support" icon={MessageSquare} title={tr("Offene Supportfälle")} c={cr.support} nav={nav} />
        </div>
      </section>

      {/* ===== 3. DREI HAUPTBOXEN ===== */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="section-mainboxes">
        {/* Arbeit heute */}
        <Card className="p-5" data-testid="box-work">
          <BoxTitle icon={ClipboardList}>{tr("Arbeit heute")}</BoxTitle>
          <div className="space-y-1.5 mt-3">
            <WorkRow icon={Inbox} label={tr("Neue Anfragen")} n={cards.leads?.new} unit={tr("neu")} tone={cards.leads?.new ? "lime" : "gray"} to="/admin/leads" nav={nav} />
            <WorkRow icon={KanbanSquare} label={tr("Offene Aufgaben")} n={cards.tasks?.open} unit={tr("offen")} tone={cards.tasks?.overdue ? "red" : "lime"} to="/admin/tasks" nav={nav} />
            <WorkRow icon={MessageSquare} label={tr("Supportfälle")} n={cards.support?.open} unit={tr("offen")} tone={cards.support?.open ? "amber" : "gray"} to="/admin/support" nav={nav} />
            <WorkRow icon={BadgeCheck} label={tr("Kunden-Freigaben")} n={cards.tasks?.waiting_client} unit={tr("warten")} tone={cards.tasks?.waiting_client ? "amber" : "gray"} to="/admin/tasks" nav={nav} />
            <WorkRow icon={FileText} label={tr("Monatsreport fällig")} n={wk.reports_to_send} unit={tr("fällig")} tone={wk.reports_to_send ? "red" : "gray"} to="/admin/reports" nav={nav} />
          </div>
        </Card>

        {/* Geld & Chancen */}
        <Card className="p-5" data-testid="box-money">
          <BoxTitle icon={Wallet}>{tr("Geld & Chancen")}</BoxTitle>
          <div className="space-y-1.5 mt-3">
            <MoneyRow icon={TrendingUp} label={tr("Monatsumsatz (aktiv)")} value={chf(money.mrr)} />
            <MoneyRow icon={ReceiptText} label={tr("Offene Rechnungen")} value={chf(money.open_invoices_amount)} note={cards.invoices?.open ? `${cards.invoices.open} ${tr("offen")}` : ""} />
            <MoneyRow icon={Sparkles} label={tr("Zusatzarbeit erkannt")} value={au.zusatzarbeit_open ?? 0} note={tr("Chancen")} action={tr("Ansehen")} to="/admin/next-steps" nav={nav} />
            <MoneyRow icon={FileText} label={tr("Angebote vorbereiten")} value={money.prepared_offers ?? 0} note={tr("Angebote")} action={tr("Ansehen")} to="/admin/next-steps" nav={nav} />
            <MoneyRow icon={Banknote} label="bexio" value={money.bexio_connected ? tr("Konfiguriert") : tr("Intern")} note={tr("Live-Status offen")} dotColor="gray" />
          </div>
        </Card>

        {/* Heute / Diese Woche */}
        <Card className="p-5 flex flex-col" data-testid="box-week">
          <BoxTitle icon={CalendarCheck}>{tr("Heute / Diese Woche")}</BoxTitle>
          <div className="space-y-2.5 mt-3 flex-1">
            <WeekRow icon={Inbox} text={tr("{n} Anfragen beantworten").replace("{n}", wk.leads_to_answer ?? 0)} done={!wk.leads_to_answer} />
            <WeekRow icon={ReceiptText} text={tr("{n} Rechnung(en) prüfen").replace("{n}", wk.invoices_to_check ?? 0)} done={!wk.invoices_to_check} />
            <WeekRow icon={FileText} text={tr("{n} Monatsreport(s) senden").replace("{n}", wk.reports_to_send ?? 0)} done={!wk.reports_to_send} />
            <WeekRow icon={Sparkles} text={tr("{n} Angebote vorbereiten").replace("{n}", wk.offers_to_prepare ?? 0)} done={!wk.offers_to_prepare} />
            <WeekRow icon={DatabaseBackup} text={cr.backup?.count ? tr("Backup prüfen") : tr("Backups aktuell")} done={!cr.backup?.count} />
          </div>
          <button onClick={() => nav("/admin/tasks")} data-testid="week-plan-btn"
            className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-lg text-sm font-semibold">{tr("Zur Wochenplanung")}</button>
        </Card>
      </section>

      {/* ===== OFFENE KI-WÜNSCHE ===== */}
      {wishStats && (
        <button onClick={() => nav("/admin/ai-requests")} data-testid="dashboard-wish-strip"
          className="w-full text-left flex items-center gap-4 flex-wrap px-5 py-4 rounded-xl border border-[#A3E635]/20 bg-[#A3E635]/[0.05] hover:border-[#A3E635]/40 transition-colors">
          <div className="w-10 h-10 rounded-xl bg-[#A3E635]/12 flex items-center justify-center shrink-0">
            <Lightbulb className="w-5 h-5 text-[#A3E635]" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-white">{tr("Offene Kundenwünsche")}</div>
            <div className="text-xs text-gray-400">
              {wishStats.offen > 0
                ? tr("{neu} neu · {pruef} in Prüfung · warten auf Bearbeitung durch KEWAG")
                    .replace("{neu}", wishStats.neu).replace("{pruef}", wishStats.in_pruefung)
                : tr("Keine offenen Wünsche — alles bearbeitet.")}
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
            {wishStats.overdue > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-300" data-testid="wish-overdue"
                title={tr("Offene Wünsche ohne Bearbeitung seit über 7 Tagen")}>
                <AlertTriangle className="w-3 h-3" /> {tr("{n} überfällig").replace("{n}", wishStats.overdue)}
              </span>
            )}
            {typeof wishStats.this_week === "number" && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/5 border border-white/10" data-testid="wish-week-trend"
                title={tr("Diese Woche {c} · Vorwoche {p}").replace("{c}", wishStats.this_week).replace("{p}", wishStats.prev_week)}>
                <span className="text-gray-300">{tr("{n} diese Woche").replace("{n}", wishStats.this_week)}</span>
                {(() => {
                  const dv = wishStats.week_delta || 0;
                  const DI = dv > 0 ? TrendingUp : dv < 0 ? TrendingDown : Minus;
                  const cls = dv > 0 ? "text-[#A3E635]" : dv < 0 ? "text-amber-400" : "text-gray-500";
                  return <span className={`inline-flex items-center gap-0.5 ${cls}`} data-testid="wish-week-delta"><DI className="w-3 h-3" />{dv > 0 ? `+${dv}` : dv}</span>;
                })()}
              </span>
            )}
            {wishStats.mit_bild > 0 && (
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300" data-testid="wish-with-image">
                {tr("{n} mit Bild").replace("{n}", wishStats.mit_bild)}
              </span>
            )}
            <span className={`text-2xl font-black ${wishStats.offen > 0 ? "text-[#A3E635]" : "text-gray-500"}`} data-testid="wish-open-count">{wishStats.offen}</span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#A3E635]">{tr("Öffnen")} <ArrowRight className="w-3.5 h-3.5" /></span>
          </div>
        </button>
      )}

      {/* ===== FRONTEND-FEHLER (abgefangen) ===== */}
      {feErr && (
        <button onClick={() => nav("/admin/audit-log?tab=frontend")} data-testid="dashboard-fe-errors-strip"
          className={`w-full text-left flex items-center gap-4 flex-wrap px-5 py-4 rounded-xl border transition-colors ${feErr.recent > 0 ? "border-amber-400/25 bg-amber-400/[0.05] hover:border-amber-400/45" : "border-[#A3E635]/20 bg-[#A3E635]/[0.05] hover:border-[#A3E635]/40"}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${feErr.recent > 0 ? "bg-amber-400/12" : "bg-[#A3E635]/12"}`}>
            <MonitorX className={`w-5 h-5 ${feErr.recent > 0 ? "text-amber-400" : "text-[#A3E635]"}`} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-white">{tr("Frontend-Fehler (abgefangen)")}</div>
            <div className="text-xs text-gray-400">
              {feErr.recent > 0
                ? tr("{n} in den letzten 7 Tagen · sicher abgefangen, Kunden sahen eine freundliche Meldung").replace("{n}", feErr.recent)
                : tr("Keine abgefangenen Fehler in den letzten 7 Tagen gemeldet.")}
              {feErr.latest ? " · " + tr("zuletzt:") + " " + feErr.latest : ""}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className={`text-2xl font-black ${feErr.recent > 0 ? "text-amber-400" : "text-gray-500"}`} data-testid="fe-error-count">{feErr.recent}</span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#A3E635]">{tr("Öffnen")} <ArrowRight className="w-3.5 h-3.5" /></span>
          </div>
        </button>
      )}

      {/* ===== KUNDEN-APP-ADAPTION ===== */}
      {pwa && (
        <div data-testid="admin-pwa-adoption" className="flex items-center gap-4 flex-wrap px-5 py-4 rounded-xl border border-[#A3E635]/20 bg-[#A3E635]/[0.05]">
          <div className="w-10 h-10 rounded-xl bg-[#A3E635]/12 flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-[#A3E635]" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-white">{tr("Kunden-App-Adaption (PWA)")}</div>
            <div className="text-xs text-gray-400">
              {pwa.installed_clients > 0
                ? tr("{c} von {t} Kunden nutzen KEWAGpage als App · {u} {unit}")
                    .replace("{c}", pwa.installed_clients).replace("{t}", pwa.active_clients)
                    .replace("{u}", pwa.installed_users).replace("{unit}", pwa.installed_users === 1 ? tr("Gerät") : tr("Geräte"))
                : tr("Noch kein Kunde hat KEWAGpage als App installiert.")}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <span className="text-2xl font-black text-[#A3E635]" data-testid="pwa-pct">{pwa.pct_clients}%</span>
            {["ios", "android", "desktop"].map((p) => (
              (pwa.by_platform?.[p] || 0) > 0 ? (
                <span key={p} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300">
                  {{ ios: "iOS", android: "Android", desktop: "Desktop" }[p]}: {pwa.by_platform[p]}
                </span>
              ) : null
            ))}
          </div>
        </div>
      )}

      {/* ===== KEWAG-TEAM ===== */}
      <section data-testid="section-team">
        <SectionTitle>{tr("KEWAG-Team")}</SectionTitle>
        <Card className="p-0 overflow-hidden" data-testid="dashboard-team">
          <button onClick={() => nav("/admin/security?tab=team")} data-testid="team-open-security"
            className="w-full px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-3 hover:bg-white/[0.02] transition-colors">
            <span className="flex items-center gap-2 font-bold text-sm"><Users className="w-4 h-4 text-[#A3E635]" /> {tr("Internes Team verwalten")}</span>
            <span className="flex items-center gap-1 text-xs text-gray-400">{tr("Benutzer & Sicherheit")} <ChevronRight className="w-3.5 h-3.5" /></span>
          </button>
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <TeamStat icon={Users} label={tr("Aktive Mitglieder")} value={tm.active ?? 0} accent="lime" testid="team-tile-active" />
              <TeamStat icon={Mail} label={tr("Offene Einladungen")} value={tm.invites_open ?? 0} accent={tm.invites_open ? "amber" : "gray"} testid="team-tile-invites" />
              <TeamStat icon={ShieldAlert} label={tr("2FA offen")} value={tm.twofa_open ?? 0} accent={tm.twofa_open ? "amber" : "gray"} testid="team-tile-twofa" />
              <TeamStat icon={AlertTriangle} label={tr("Kunden ohne Team")} value={tm.clients_without_team ?? 0} accent={tm.clients_without_team ? "red" : "gray"} testid="team-tile-noteam" />
            </div>

            {(tm.roles || []).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="team-roles">
                {tm.roles.map((r) => (
                  <span key={r.key} className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-gray-300">
                    {r.label} <span className="text-[#A3E635]">{r.count}</span>
                  </span>
                ))}
              </div>
            )}

            {tm.clients_without_team > 0 && (
              <button onClick={() => nav("/admin/security?tab=assign")} data-testid="team-no-care-warning"
                className="w-full text-left rounded-lg border border-amber-400/30 bg-amber-400/[0.07] px-4 py-3 flex items-start gap-2.5 hover:border-amber-400/50 transition-colors">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <span className="text-xs text-amber-200">
                  {tr("Noch kein Betreuungsteam zugewiesen")}: {(tm.unassigned_clients || []).map((c) => c.name).join(", ")}.
                  <span className="text-amber-300/80"> {tr("Jetzt zuweisen →")}</span>
                </span>
              </button>
            )}

            <div className="space-y-1.5">
              {(tm.members || []).map((m) => (
                <div key={m.id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`team-member-${m.id}`}>
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs font-bold text-gray-300 shrink-0">
                    {(m.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm flex items-center gap-1.5 min-w-0">
                      {m.team_role === "super_admin" && <Crown className="w-3.5 h-3.5 text-[#A3E635] shrink-0" />}<span className="truncate" title={m.name}>{m.name}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">{m.team_role_label}{m.invited && <span className="text-amber-400" data-testid={`team-member-invited-${m.id}`}> · {tr("noch nicht angemeldet")}</span>}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold">{m.client_count}</div>
                    <div className="text-[10px] text-gray-500">{m.client_count === 1 ? tr("Kunde") : tr("Kunden")}</div>
                  </div>
                  {!m.twofa && <ShieldAlert className="w-3.5 h-3.5 text-amber-400 shrink-0" title={tr("2FA offen")} />}
                </div>
              ))}
            </div>
          </div>
        </Card>
      </section>

      {/* ===== 4. KUNDEN IM BLICK ===== */}
      <section className="min-w-0 max-w-full" data-testid="section-clients">
        <div className="flex min-w-0 max-w-full flex-col items-stretch gap-3 mb-4 md:flex-row md:items-center md:justify-between md:flex-wrap">
          <SectionTitle>{tr("Kunden im Blick")}</SectionTitle>
          <div className="flex w-full min-w-0 max-w-full flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center md:w-auto" data-testid="clients-controls">
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-1 bg-[#121217] border border-white/10 rounded-lg p-1" data-testid="clients-filter">
              {FILTERS.map(([v, l]) => (
                <button key={v} onClick={() => setCFilter(v)} data-testid={`clients-filter-${v}`}
                  className={`min-w-0 min-h-10 break-words text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors ${cFilter === v ? "bg-[#A3E635] text-black" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>{l}</button>
              ))}
            </div>
            <div className="relative w-full min-w-0 max-w-full sm:w-auto">
              <ArrowDownUp className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select value={cSort} onChange={(e) => setCSort(e.target.value)} data-testid="clients-sort"
                className="w-full min-w-0 max-w-full bg-[#121217] border border-white/10 rounded-lg text-xs text-gray-200 pl-8 pr-3 py-2 outline-none focus:border-[#A3E635] appearance-none cursor-pointer">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{tr("Sortieren: {l}").replace("{l}", l)}</option>)}
              </select>
            </div>
          </div>
        </div>
        <Card className="p-0 overflow-hidden">
          <div className="hidden min-[1900px]:block overflow-x-auto" data-testid="clients-table-desktop">
            <table className="w-full text-sm min-w-[1180px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/8">
                  <SortTh label={tr("Kunde")} k="name" cSort={cSort} setCSort={setCSort} align="left" px="px-5" />
                  <th className="text-left font-semibold px-3 py-3">{tr("Domain")}</th>
                  <th className="text-left font-semibold px-3 py-3">{tr("Paket")}</th>
                  <th className="text-left font-semibold px-3 py-3">{tr("Status")}</th>
                  <th className="text-center font-semibold px-3 py-3">WordPress</th>
                  <th className="text-center font-semibold px-3 py-3">{tr("Formulare")}</th>
                  <th className="text-center font-semibold px-3 py-3">{tr("Analytics")}</th>
                  <th className="text-center font-semibold px-3 py-3">Site Kit</th>
                  <th className="text-center font-semibold px-3 py-3">{tr("Backup")}</th>
                  <th className="text-center font-semibold px-3 py-3">{tr("Sicherheit")}</th>
                  <th className="text-left font-semibold px-3 py-3">{tr("Letzte Live-Prüfung")}</th>
                  <th className="text-left font-semibold px-3 py-3">{tr("Nächste Aktion")}</th>
                  <th className="px-4 py-3 text-right">{tr("Aktion")}</th>
                </tr>
              </thead>
              <tbody>
                {clientRows.map((cl) => (
                  <tr key={cl.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`client-row-${cl.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-black shrink-0"
                          style={{ background: cl.primary_color || "#A3E635" }}>{initials(cl.company_name)}</span>
                        <div className="font-semibold text-white truncate max-w-[180px]">{cl.company_name}</div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-400 truncate max-w-[170px]">{cl.domain || "—"}</td>
                    <td className="px-3 py-3 text-gray-300 whitespace-nowrap">{cl.package}</td>
                    <td className="px-3 py-3"><StatusPill status={cl.status} attention={cl.attention} /></td>
                    <td className="px-3 py-3"><CellState st={cl.wp} sub={cl.wp_version} testid={`cell-wp-${cl.id}`} /></td>
                    <td className="px-3 py-3"><CellState st={cl.forms} sub={cl.forms?.value} testid={`cell-forms-${cl.id}`} /></td>
                    <td className="px-3 py-3"><CellState st={cl.analytics} sub={cl.analytics?.value} testid={`cell-analytics-${cl.id}`} /></td>
                    <td className="px-3 py-3"><CellState st={cl.site_kit} testid={`cell-sitekit-${cl.id}`} /></td>
                    <td className="px-3 py-3"><CellState st={cl.backup} testid={`cell-backup-${cl.id}`} /></td>
                    <td className="px-3 py-3"><CellState st={cl.security} sub={cl.security?.value} testid={`cell-security-${cl.id}`} /></td>
                    <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">{cl.last_check ? smartDate(cl.last_check) : "—"}</td>
                    <td className="px-3 py-3 text-xs text-gray-300 max-w-[180px]">{cl.next_action}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                        {canWriteClients && <button onClick={() => checkOne(cl.id)} disabled={checkingId === cl.id} data-testid={`client-check-${cl.id}`}
                          className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-2.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                          <RefreshCw className={`w-3 h-3 ${checkingId === cl.id ? "animate-spin" : ""}`} /> {tr("Live prüfen")}
                        </button>}
                        <button onClick={() => nav(`/admin/clients/${cl.id}`)} data-testid={`client-open-${cl.id}`}
                          className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs font-semibold">{tr("Öffnen")}</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {clientRows.length === 0 && (
                  <tr><td colSpan={12} className="px-5 py-8 text-center text-gray-500" data-testid="clients-empty">{tr("Keine Kunden für diesen Filter.")}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile-Karten */}
          <div className="min-[1900px]:hidden divide-y divide-white/5" data-testid="clients-mobile">
            {clientRows.length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-500" data-testid="clients-empty-mobile">{tr("Keine Kunden für diesen Filter.")}</div>
            ) : clientRows.map((cl) => (
              <div key={cl.id} className="p-4" data-testid={`client-card-${cl.id}`}>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-black shrink-0" style={{ background: cl.primary_color || "#A3E635" }}>{initials(cl.company_name)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-white truncate">{cl.company_name}</div>
                    <div className="text-xs text-gray-500 truncate">{cl.domain || "—"}</div>
                  </div>
                  <StatusPill status={cl.status} attention={cl.attention} />
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-[11px]">
                  <span className="px-2 py-0.5 rounded-md bg-white/5 text-gray-300">{cl.package}</span>
                  <MStat label="WordPress" st={cl.wp} />
                  <MStat label={tr("Formulare")} st={cl.forms} />
                  <MStat label="Analytics" st={cl.analytics} />
                  <MStat label={tr("Backup")} st={cl.backup} />
                  <MStat label={tr("Sicherheit")} st={cl.security} />
                </div>
                {cl.next_action && <div className="text-xs text-gray-400 mt-2.5 flex items-start gap-1.5"><ArrowRight className="w-3.5 h-3.5 text-[#A3E635] shrink-0 mt-0.5" /> {cl.next_action}</div>}
                <div className="flex gap-2 mt-3 sm:justify-end">
                  {canWriteClients && <button onClick={() => checkOne(cl.id)} disabled={checkingId === cl.id} data-testid={`client-card-check-${cl.id}`}
                    className="flex-1 sm:flex-none sm:min-w-[160px] sm:px-5 bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                    <RefreshCw className={`w-3.5 h-3.5 ${checkingId === cl.id ? "animate-spin" : ""}`} /> {tr("Live prüfen")}
                  </button>}
                  <button onClick={() => nav(`/admin/clients/${cl.id}`)} data-testid={`client-card-open-${cl.id}`}
                    className="flex-1 sm:flex-none sm:min-w-[160px] sm:px-5 bg-[#A3E635] hover:bg-[#BEF264] text-black py-2.5 rounded-lg text-xs font-bold">{tr("Öffnen")}</button>
                </div>
              </div>
            ))}
          </div>

          <div className="px-5 py-3 border-t border-white/8 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-3">
              <button onClick={() => nav("/admin/clients")} data-testid="all-clients-btn"
                className="bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-lg text-sm font-semibold">{tr("Alle Kunden anzeigen")}</button>
              <span className="text-[11px] text-gray-500" data-testid="clients-count">{tr("{n} von {total} Kunden").replace("{n}", clientRows.length).replace("{total}", allClients.length)}</span>
            </div>
            <span className="text-[11px] text-gray-500 flex items-center gap-1"><Server className="w-3 h-3" /> {tr("Quelle: gespeicherte Connector-Prüfungen")}</span>
          </div>
        </Card>
      </section>

      {/* ===== 5. KEWAGPage CONTROL OS (globaler Plattform-Status) + ANALYTICS ===== */}
      <section className="space-y-6" data-testid="section-analytics">
        <ControlOsCard co={d.control_os} tr={tr} />

        <AnalyticsBoard nav={nav} />
      </section>

      {/* ===== 6. BACKUP / WICHTIG / AKTIVITÄTEN ===== */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="section-backup-row">
        <Card className="p-5 flex flex-col" data-testid="card-backup">
          <div className="flex items-center justify-between mb-3">
            <BoxTitle icon={DatabaseBackup}>{tr("Backup-System")}</BoxTitle>
            <span className={`text-[11px] font-semibold ${bk.missing ? TXT.red : bk.old ? TXT.amber : TXT.green}`}>{bk.missing ? tr("Fehlt") : bk.old ? tr("Veraltet") : tr("Erfolgreich")}</span>
          </div>
          <div className="space-y-2 text-sm flex-1">
            <KV k={tr("Letztes In-App-Backup")} v={smartDate(bk.last)} />
            <KV k={tr("Vollsicherung und Zeitplan")} v={tr("Unter Sicherheit & QA prüfen")} />
          </div>
          <button onClick={() => nav("/admin/qa?focus=backup")} data-testid="open-backup-btn"
            className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-lg text-sm font-semibold">{tr("Backup & Wiederherstellung öffnen")}</button>
        </Card>

        <Card className="p-5 flex flex-col" data-testid="card-todos">
          <BoxTitle icon={Zap}>{tr("Was jetzt wichtig ist")}</BoxTitle>
          <div className="space-y-2 mt-3 flex-1">
            {(d.todos || []).slice(0, 5).map((t) => (
              <div key={t.id} className="flex items-center gap-2.5" data-testid={`todo-${t.id}`}>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${(PRIO[t.priority] || PRIO.mittel).cls} bg-white/5 shrink-0`}>{tr((PRIO[t.priority] || PRIO.mittel).label)}</span>
                <span className="text-sm text-gray-200 truncate flex-1">{t.title}</span>
                <span className="text-[10px] text-gray-500 shrink-0">{t.due ? smartDate(t.due).split(",")[0] : ""}</span>
              </div>
            ))}
            {(d.todos || []).length === 0 && <div className="text-sm text-gray-500 py-4 text-center">{tr("Alles erledigt.")} 🎉</div>}
          </div>
          <button onClick={() => nav("/admin/tasks")} data-testid="all-todos-btn"
            className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-lg text-sm font-semibold">{tr("Zur vollständigen Aufgabenliste")}</button>
        </Card>

        <Card className="p-5 flex flex-col" data-testid="card-activity">
          <BoxTitle icon={History}>{tr("Letzte Aktivitäten")}</BoxTitle>
          <div className="space-y-2.5 mt-3 flex-1">
            {(d.activity || []).slice(0, 6).map((a, i) => (
              <div key={i} className="flex items-center gap-2.5" data-testid={`activity-${i}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${a.category === "backup" ? DOT.green : a.category === "security" ? DOT.red : "bg-sky-400"}`} />
                <span className="text-xs text-gray-300 truncate flex-1"><b className="text-gray-200">{a.company_name}:</b> {a.text}</span>
                <span className="text-[10px] text-gray-500 shrink-0">{fmtTime(a.created_at)}</span>
              </div>
            ))}
            {(d.activity || []).length === 0 && <div className="text-sm text-gray-500 py-4 text-center">{tr("Keine Aktivitäten.")}</div>}
          </div>
          <button onClick={() => nav("/admin/audit-log")} data-testid="open-protocol-btn"
            className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-lg text-sm font-semibold">{tr("Zum Protokoll")}</button>
        </Card>
      </section>

      {/* ===== 7. SCHNELLAKTIONEN / MODULE / UPSELL ===== */}
      <section className={`grid grid-cols-1 ${canWriteClients ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-6 pb-4`} data-testid="section-bottom">
        {canWriteClients && <Card className="p-5" data-testid="card-quick">
          <BoxTitle icon={Zap}>{tr("Schnellaktionen")}</BoxTitle>
          <div className="grid grid-cols-3 gap-2.5 mt-3">
            {canCreateClient && <QuickBtn icon={Users} label={tr("Kunde anlegen")} to="/admin/clients/new" nav={nav} tid="quick-new-client" />}
            <QuickBtn icon={Inbox} label={tr("Anfrage erfassen")} to="/admin/leads" nav={nav} tid="quick-leads" />
            <QuickBtn icon={KanbanSquare} label={tr("Aufgabe erstellen")} to="/admin/tasks" nav={nav} tid="quick-tasks" />
            <QuickBtn icon={ReceiptText} label={tr("Rechnung erstellen")} to="/admin/invoices" nav={nav} tid="quick-invoice" />
            {isSuperAdmin && <QuickBtn icon={DatabaseBackup} label={tr("Backup erstellen")} to="/admin/backup" nav={nav} tid="quick-backup" />}
            {isSuperAdmin && <QuickBtn icon={ShieldCheck} label={tr("Sicherheit prüfen")} to="/admin/qa" nav={nav} tid="quick-qa" />}
          </div>
        </Card>}

        <Card className="p-5 flex flex-col" data-testid="card-modules">
          <BoxTitle icon={Boxes}>{tr("Module & Booster")}</BoxTitle>
          <div className="grid grid-cols-2 gap-2.5 mt-3 flex-1">
            {(d.systems?.module || []).slice(0, 6).map((m, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`module-${i}`}>
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${m.active ? DOT.green : DOT.gray}`} />
                  <span className="text-xs font-semibold text-white truncate">{m.name}</span>
                </div>
                <div className={`text-[10px] mt-0.5 ${m.active ? "text-[#A3E635]" : "text-gray-500"}`}>{m.active ? tr("Aktiv") : tr("Geplant")}</div>
              </div>
            ))}
          </div>
          <button onClick={() => nav("/admin/modules")} data-testid="all-modules-btn"
            className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-lg text-sm font-semibold">{tr("Alle Module verwalten")}</button>
        </Card>

        <Card className="p-5 flex flex-col" data-testid="card-upsell">
          <div className="flex items-center justify-between">
            <BoxTitle icon={Crown}>{tr("Upsell-Chancen")}</BoxTitle>
            <span className="text-[10px] text-gray-500">{tr("{n} offen").replace("{n}", money.upsell_count || 0)}</span>
          </div>
          <div className="space-y-2 mt-3 flex-1">
            {(d.upsell_list || []).slice(0, 5).map((u, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`upsell-${i}`}>
                <PiggyBank className="w-3.5 h-3.5 text-[#A3E635] shrink-0" />
                <span className="text-xs text-gray-200 truncate flex-1">{u.client} — {u.title}</span>
                <span className={`text-[10px] font-semibold shrink-0 ${(PRIO[u.priority] || PRIO.mittel).cls}`}>{tr((PRIO[u.priority] || PRIO.mittel).label)}</span>
              </div>
            ))}
            {(d.upsell_list || []).length === 0 && <div className="text-sm text-gray-500 py-4 text-center">{tr("Keine offenen Chancen.")}</div>}
          </div>
          <button onClick={() => nav("/admin/next-steps")} data-testid="all-upsell-btn"
            className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-lg text-sm font-semibold">{tr("Alle Chancen anzeigen")}</button>
        </Card>
      </section>

        </div>
      </details>
      {/* KEWAGportal: nur kleiner Link */}
      <div className="text-center pb-2">
        <span className="text-[11px] text-gray-600">{tr("KEWAGportal Control OS — separates System für grosse Portale ·")}
          <span className="text-gray-500"> {tr("noch nicht verbunden")}</span></span>
      </div>
    </div>
  );
}

/* ---------- Sub-Komponenten ---------- */
const SectionTitle = ({ children }) => <h2 className="text-lg font-bold text-white mb-4 tracking-tight">{children}</h2>;

const TEAM_STAT_ACCENT = { lime: "text-[#A3E635]", amber: "text-amber-400", red: "text-red-400", gray: "text-gray-300" };
const TeamStat = ({ icon: Icon, label, value, accent = "gray", testid }) => (
  <div className="bg-black/20 border border-white/10 rounded-xl p-3.5" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-1.5">{Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}<span className="truncate">{label}</span></div>
    <div className={`text-2xl font-black ${TEAM_STAT_ACCENT[accent] || "text-white"}`}>{value}</div>
  </div>
);
const BoxTitle = ({ icon: Icon, children }) => (
  <h3 className="text-base font-bold text-white flex items-center gap-2"><Icon className="w-4 h-4 text-[#A3E635]" /> {children}</h3>
);

const TREND_ICONS = { leads: Inbox, clients: Users, backups: DatabaseBackup, invoices: ReceiptText };
function TrendStrip({ wt }) {
  const { t: tr } = useI18n();
  return (
    <section data-testid="section-trend">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <SectionTitle>{tr("Verlauf · {cur} vs. {prev}").replace("{cur}", wt.label_current).replace("{prev}", wt.label_prev)}</SectionTitle>
        <span className="text-[11px] text-gray-500 flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" /> {tr("{cur} gegen Vorwoche {prev}").replace("{cur}", wt.range_current).replace("{prev}", wt.range_prev)}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {(wt.metrics || []).map((m) => <TrendCard key={m.key} m={m} Icon={TREND_ICONS[m.key] || Activity} />)}
      </div>
    </section>
  );
}

function TrendCard({ m, Icon }) {
  const { t: tr } = useI18n();
  const isChf = m.unit === "chf";
  const fmt = (v) => (isChf ? chf(v) : (v ?? 0).toLocaleString("de-CH"));
  const delta = m.delta || 0;
  const up = delta > 0, down = delta < 0;
  const DeltaIcon = up ? TrendingUp : down ? TrendingDown : Minus;
  const dcls = up ? "text-[#A3E635] bg-[#A3E635]/10" : down ? "text-amber-400 bg-amber-500/10" : "text-gray-500 bg-white/5";
  const pct = m.delta_pct;
  const deltaText = m.previous === 0
    ? (m.current ? tr("Neu") : tr("Keine Änderung"))
    : `${up ? "+" : ""}${isChf ? chf(delta) : delta}${pct != null ? ` · ${up ? "+" : ""}${pct}%` : ""}`;
  return (
    <div className="rounded-xl border border-white/10 bg-[#121217] p-4" data-testid={`trend-${m.key}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 leading-tight">{tr(m.label)}</span>
        <Icon className="w-4 h-4 text-gray-500 shrink-0" />
      </div>
      <div className="flex items-end gap-2 mt-2 flex-wrap">
        <span className="text-3xl font-black text-white tracking-tighter" data-testid={`trend-${m.key}-current`}>{fmt(m.current)}</span>
        <span className={`mb-1 inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded-md ${dcls}`} data-testid={`trend-${m.key}-delta`}>
          <DeltaIcon className="w-3 h-3" /> {deltaText}
        </span>
      </div>
      <div className="text-[11px] text-gray-500 mt-1.5">{tr("Vorwoche:")} <span className="text-gray-400">{fmt(m.previous)}</span></div>
      <div className="mt-2 pt-2 border-t border-white/5 flex items-center gap-1 text-[10px] text-gray-600">
        <Server className="w-3 h-3 shrink-0" /> {m.source}
      </div>
    </div>
  );
}

function SortTh({ label, k, cSort, setCSort, align = "left", px = "px-3" }) {
  const active = cSort === k;
  const just = align === "center" ? "justify-center" : align === "right" ? "justify-end" : "justify-start";
  return (
    <th className={`${px} py-3`}>
      <button onClick={() => setCSort(k)} data-testid={`sort-${k}`}
        className={`w-full flex items-center gap-1 ${just} font-semibold uppercase tracking-wider text-[11px] transition-colors ${active ? "text-[#A3E635]" : "text-gray-500 hover:text-gray-300"}`}>
        {label} <ArrowDownUp className="w-3 h-3 opacity-70" />
      </button>
    </th>
  );
}

function CritCard({ icon: Icon, title, c = {}, testid, nav }) {
  const color = c.color || "gray";
  const HEX = { green: "#A3E635", amber: "#F59E0B", red: "#EF4444", gray: "#6b7280" };
  const hex = HEX[color] || HEX.gray;
  return (
    <button onClick={() => c.route && nav(c.route)} data-testid={testid}
      className={`group relative overflow-hidden text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5 ${BORD[color]} ${TINT[color]}`}>
      <span className="absolute top-0 inset-x-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: hex }} />
      <div className="flex items-start justify-between">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${hex}1F`, color: hex }}>
          <Icon className="w-4.5 h-4.5" />
        </span>
        {c.route && <ArrowRight className="w-4 h-4 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
      <div className={`text-[28px] leading-none font-black mt-3 ${TXT[color]}`}>{c.count ?? 0}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-2 leading-tight [overflow-wrap:anywhere]">{title}</div>
      <div className="text-[11px] text-gray-400 mt-0.5">{c.label || "—"}</div>
    </button>
  );
}

const TONEC = { lime: "text-[#A3E635]", red: "text-red-400", amber: "text-amber-400", gray: "text-gray-500" };
function WorkRow({ icon: Icon, label, n, unit, tone = "gray", to, nav }) {
  const { t: tr } = useI18n();
  return (
    <div className="flex items-center gap-3 px-1 py-1.5 rounded-lg hover:bg-white/[0.03]">
      <Icon className="w-4 h-4 text-gray-500 shrink-0" />
      <span className="text-sm text-gray-300 flex-1">{label}</span>
      <span className={`text-sm font-bold ${TONEC[tone]}`}>{n ?? 0}</span>
      <span className="text-[11px] text-gray-500 w-12">{unit}</span>
      <button onClick={() => nav(to)} className="text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1 rounded-md">{tr("Öffnen")}</button>
    </div>
  );
}

function MoneyRow({ icon: Icon, label, value, note, action, to, nav, dotColor }) {
  return (
    <div className="flex items-center gap-3 px-1 py-1.5 rounded-lg hover:bg-white/[0.03]">
      {dotColor ? <span className={`w-2.5 h-2.5 rounded-full ${DOT[dotColor]} shrink-0`} /> : <Icon className="w-4 h-4 text-gray-500 shrink-0" />}
      <span className="text-sm text-gray-300 flex-1">{label}</span>
      <span className="text-sm font-bold text-white whitespace-nowrap">{value}</span>
      {note && <span className="text-[11px] text-gray-500 w-16 text-right">{note}</span>}
      {action && <button onClick={() => nav(to)} className="text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1 rounded-md">{action}</button>}
    </div>
  );
}

function WeekRow({ icon: Icon, text, done }) {
  return (
    <div className="flex items-center gap-2.5">
      {done ? <CheckCircle2 className="w-4 h-4 text-[#A3E635] shrink-0" /> : <Icon className="w-4 h-4 text-amber-400 shrink-0" />}
      <span className={`text-sm ${done ? "text-gray-500 line-through" : "text-gray-200"}`}>{text}</span>
    </div>
  );
}

function StatusPill({ status, attention }) {
  const { t: tr } = useI18n();
  const map = { aktiv: "Aktiv", in_vorbereitung: "In Vorbereitung", pausiert: "Pausiert", gekuendigt: "Gekündigt" };
  const col = attention === "rot" ? "red" : attention === "gelb" ? "amber" : "green";
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md border ${BORD[col]} ${TXT[col]}`}><span className={`w-1.5 h-1.5 rounded-full ${DOT[col]}`} />{tr(map[status] || status || "Aktiv")}</span>;
}

function CellState({ st, sub, testid }) {
  const color = st?.color || "gray";
  const Ic = color === "green" ? CheckCircle2 : color === "red" ? AlertTriangle : color === "amber" ? AlertTriangle : WifiOff;
  return (
    <div className="flex flex-col items-center gap-0.5" data-testid={testid} title={st?.label || "—"}>
      <Ic className={`w-4 h-4 ${TXT[color]}`} />
      {sub && <span className="text-[10px] text-gray-500 truncate max-w-[80px]">{sub}</span>}
    </div>
  );
}

function MStat({ label, st }) {
  const color = st?.color || "gray";
  const dot = { green: "bg-[#A3E635]", red: "bg-red-400", amber: "bg-amber-400", gray: "bg-gray-600" }[color] || "bg-gray-600";
  return (
    <span className="inline-flex items-center gap-1 text-gray-400">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} /> {label}
    </span>
  );
}

function CountBadge({ n, tone = "amber" }) {
  if (!n) return <span className="text-gray-600">0</span>;
  const cls = tone === "red" ? "bg-red-500/15 text-red-300 border-red-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30";
  return <span className={`inline-block min-w-[22px] text-center text-xs font-bold px-1.5 py-0.5 rounded-md border ${cls}`}>{n}</span>;
}

function KV({ k, v }) {
  return <div className="flex items-center justify-between"><span className="text-gray-500">{k}</span><span className="text-gray-200 font-medium text-right">{v}</span></div>;
}

function QuickBtn({ icon: Icon, label, to, nav, tid }) {
  return (
    <button onClick={() => nav(to)} data-testid={tid}
      className="flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/20 transition-colors">
      <Icon className="w-4 h-4 text-[#A3E635]" />
      <span className="text-[10px] text-gray-300 text-center leading-tight">{label}</span>
    </button>
  );
}

